import { ApiService } from '../../api/api-service';
import {
  AddressValidationRequest,
  AddressValidationResult,
  CoordinatesValidationRequest,
  ReverseGeocodeResult,
} from '../../types';

const SERVICE_ROOT = '/address-validation';

/**
 * Service for Google Address Validation API calls (proxied through backend).
 * All calls go through the backend to protect the Google API key.
 */
export class AddressValidationService extends ApiService {
  /**
   * Validates an address using Google Address Validation API.
   * Returns validation status, lat/long, and optional suggested corrections.
   */
  async validateAddress(request: AddressValidationRequest): Promise<AddressValidationResult> {
    return this.handleApiCall(
      () => this.client.post<AddressValidationResult>(`${SERVICE_ROOT}/validate`, request),
      'Error validating address'
    );
  }

  /**
   * Validates coordinate ranges and optionally performs reverse geocoding.
   */
  async validateCoordinates(
    request: CoordinatesValidationRequest
  ): Promise<AddressValidationResult> {
    return this.handleApiCall(
      () =>
        this.client.post<AddressValidationResult>(`${SERVICE_ROOT}/validate-coordinates`, request),
      'Error validating coordinates'
    );
  }

  /**
   * Performs reverse geocoding to get an address from lat/long.
   * Best-effort: may return partial results.
   */
  async reverseGeocode(latitude: number, longitude: number): Promise<ReverseGeocodeResult | null> {
    try {
      return await this.handleApiCall(
        () =>
          this.client.get<ReverseGeocodeResult>(`${SERVICE_ROOT}/reverse-geocode`, {
            params: { lat: latitude, lon: longitude },
          }),
        'Error performing reverse geocode'
      );
    } catch {
      return null;
    }
  }
}
